import {
  BaseEdge,
  Edge,
  EdgeComponentWithPathOptions,
  getSmoothStepPath,
  Position,
  SmoothStepPathOptions,
} from "@xyflow/react";
import { memo } from "react";

export type AnimatedSmoothStepPathOptions = SmoothStepPathOptions & {
  pathLength: number;
};

export type AnimatedSmoothStepEdge<
  EdgeData extends Record<string, unknown> = Record<string, unknown>,
> = Edge<EdgeData, "animatedsmoothstep"> & {
  pathOptions?: AnimatedSmoothStepPathOptions;
};

type AnimatedSmoothStepEdgeProps =
  EdgeComponentWithPathOptions<AnimatedSmoothStepPathOptions>;

function createSmoothStepEdge(params: { isInternal: boolean }) {
  // eslint-disable-next-line react/display-name
  return memo(
    ({
      id,
      sourceX,
      sourceY,
      targetX,
      targetY,
      label,
      labelStyle,
      labelShowBg,
      labelBgStyle,
      labelBgPadding,
      labelBgBorderRadius,
      style,
      sourcePosition = Position.Bottom,
      targetPosition = Position.Top,
      markerEnd,
      markerStart,
      pathOptions,
      interactionWidth,
    }: AnimatedSmoothStepEdgeProps) => {
      const [path, labelX, labelY] = getSmoothStepPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
        borderRadius: pathOptions?.borderRadius,
        offset: pathOptions?.offset,
      });

      const _id = params.isInternal ? undefined : id;

      return (
        <BaseEdge
          id={_id}
          path={path}
          pathLength={pathOptions?.pathLength}
          labelX={labelX}
          labelY={labelY}
          label={label}
          labelStyle={labelStyle}
          labelShowBg={labelShowBg}
          labelBgStyle={labelBgStyle}
          labelBgPadding={labelBgPadding}
          labelBgBorderRadius={labelBgBorderRadius}
          style={style}
          markerEnd={markerEnd}
          markerStart={markerStart}
          interactionWidth={interactionWidth}
        />
      );
    },
  );
}

const AnimatedSmoothStepEdge = createSmoothStepEdge({ isInternal: false });
const AnimatedSmoothStepEdgeInternal = createSmoothStepEdge({
  isInternal: true,
});

AnimatedSmoothStepEdge.displayName = "AnimatedSmoothStepEdge";
AnimatedSmoothStepEdgeInternal.displayName = "AnimatedSmoothStepEdgeInternal";

export { AnimatedSmoothStepEdge, AnimatedSmoothStepEdgeInternal };
